import React, { useState, useEffect } from 'react';
import { uptimeRobotAPI, formatDuration, formatTimestamp } from '../../api/uptimeRobot';

export default function UptimeRobotDashboard({ theme = "light" }) {
    const [monitors, setMonitors] = useState([]);
    const [accountDetails, setAccountDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    const isDark = theme === "dark";
    const color = {
        bg: isDark ? "#1a1a1a" : "#fff",
        fg: isDark ? "#fff" : "#333",
        border: isDark ? "#333" : "#ddd",
        cardBg: isDark ? "#2d2d2d" : "#f8f9fa",
        success: "#28a745",
        warning: "#ffc107",
        danger: "#dc3545",
        info: "#17a2b8"
    };

    const fetchData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const [monitorsResult, accountResult] = await Promise.all([
                uptimeRobotAPI.getMonitors(),
                uptimeRobotAPI.getAccountDetails()
            ]);

            if (monitorsResult.success) {
                setMonitors(monitorsResult.monitors);
            } else {
                setError(monitorsResult.error);
            }

            if (accountResult.success) {
                setAccountDetails(accountResult.account);
            }

            setLastUpdate(new Date());
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        
        // Auto-refresh every 5 minutes
        const interval = setInterval(fetchData, 300000);
        return () => clearInterval(interval);
    }, []);

    const getOverallStatus = () => {
        if (monitors.length === 0) return { status: 'Unknown', color: color.info };
        
        const downMonitors = monitors.filter(m => m.status === 9 || m.status === 8);
        if (downMonitors.length > 0) {
            return { status: 'Issues Detected', color: color.danger };
        }
        
        const upMonitors = monitors.filter(m => m.status === 2);
        if (upMonitors.length === monitors.length) {
            return { status: 'All Systems Operational', color: color.success };
        }
        
        return { status: 'Partial Issues', color: color.warning };
    };

    const calculateOverallUptime = () => {
        if (monitors.length === 0) return 100;
        
        const totalUptime = monitors.reduce((acc, monitor) => {
            return acc + uptimeRobotAPI.calculateUptime(monitor);
        }, 0);
        
        return totalUptime / monitors.length;
    };

    if (error) {
        return (
            <div style={{
                padding: '1rem',
                backgroundColor: color.bg,
                color: color.fg,
                fontFamily: 'sans-serif'
            }}>
                <div style={{
                    padding: '1.5rem',
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '8px',
                    borderLeft: `4px solid ${color.danger}`
                }}>
                    <h3 style={{ margin: '0 0 1rem 0' }}>📊 UptimeRobot Connection Error</h3>
                    <p style={{ color: color.danger, fontWeight: 'bold' }}>
                        {error}
                    </p>
                    <p>This could be due to:</p>
                    <ul style={{ margin: '1rem 0', paddingLeft: '1.5rem' }}>
                        <li>UptimeRobot API key not configured on the backend</li>
                        <li>Network connectivity issues</li>
                        <li>UptimeRobot service temporarily unavailable</li>
                    </ul>
                    
                    <button
                        onClick={fetchData}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: color.info,
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginTop: '1rem'
                        }}
                    >
                        🔄 Retry Connection
                    </button>
                </div>
            </div>
        );
    }

    const overallStatus = getOverallStatus();
    const overallUptime = calculateOverallUptime();

    return (
        <div style={{
            padding: '1rem',
            backgroundColor: color.bg,
            color: color.fg,
            fontFamily: 'sans-serif'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
            }}>
                <h2 style={{ margin: 0 }}>📊 UptimeRobot Dashboard</h2>
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center'
                }}>
                    {lastUpdate && (
                        <span style={{ 
                            fontSize: '0.9rem', 
                            color: isDark ? '#ccc' : '#666' 
                        }}>
                            Last updated: {lastUpdate.toLocaleTimeString()}
                        </span>
                    )}
                    <button
                        onClick={fetchData}
                        disabled={isLoading}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: color.info,
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            opacity: isLoading ? 0.6 : 1
                        }}
                    >
                        {isLoading ? '🔄 Loading...' : '🔄 Refresh'}
                    </button>
                </div>
            </div>

            {error && (
                <div style={{
                    padding: '1rem',
                    backgroundColor: color.danger,
                    color: 'white',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                }}>
                    <strong>❌ Error:</strong> {error}
                </div>
            )}

            {/* Overall Status */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
            }}>
                <div style={{
                    padding: '1.5rem',
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '8px',
                    borderLeft: `4px solid ${overallStatus.color}`
                }}>
                    <h3 style={{ margin: '0 0 0.5rem 0' }}>🔍 Overall Status</h3>
                    <div style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: 'bold',
                        color: overallStatus.color
                    }}>
                        {overallStatus.status}
                    </div>
                </div>

                <div style={{
                    padding: '1.5rem',
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '8px'
                }}>
                    <h3 style={{ margin: '0 0 0.5rem 0' }}>📈 Overall Uptime</h3>
                    <div style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: 'bold',
                        color: overallUptime >= 99 ? color.success : overallUptime >= 95 ? color.warning : color.danger
                    }}>
                        {overallUptime.toFixed(2)}%
                    </div>
                </div>

                <div style={{
                    padding: '1.5rem',
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '8px'
                }}>
                    <h3 style={{ margin: '0 0 0.5rem 0' }}>🎯 Active Monitors</h3>
                    <div style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: 'bold',
                        color: color.info
                    }}>
                        {monitors.length}
                    </div>
                </div>

                {accountDetails && (
                    <div style={{
                        padding: '1.5rem',
                        backgroundColor: color.cardBg,
                        border: `1px solid ${color.border}`,
                        borderRadius: '8px'
                    }}>
                        <h3 style={{ margin: '0 0 0.5rem 0' }}>📧 Alert Contacts</h3>
                        <div style={{ 
                            fontSize: '1.3rem', 
                            fontWeight: 'bold',
                            color: color.info
                        }}>
                            {accountDetails.up_monitors || 0}
                        </div>
                    </div>
                )}
            </div>

            {/* Individual Monitors */}
            {monitors.length > 0 && (
                <div>
                    <h3 style={{ marginBottom: '1rem' }}>🖥️ Individual Monitors</h3>
                    <div style={{
                        display: 'grid',
                        gap: '1rem'
                    }}>
                        {monitors.map((monitor) => {
                            const statusInfo = uptimeRobotAPI.parseMonitorStatus(monitor.status);
                            const uptime = uptimeRobotAPI.calculateUptime(monitor);
                            
                            return (
                                <div
                                    key={monitor.id}
                                    style={{
                                        padding: '1.5rem',
                                        backgroundColor: color.cardBg,
                                        border: `1px solid ${color.border}`,
                                        borderRadius: '8px',
                                        borderLeft: `4px solid ${statusInfo.color}`
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '1rem'
                                    }}>
                                        <div>
                                            <h4 style={{ 
                                                margin: '0 0 0.5rem 0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                {statusInfo.icon} {monitor.friendly_name}
                                            </h4>
                                            {/* <div style={{ 
                                                fontSize: '0.9rem',
                                                color: isDark ? '#ccc' : '#666'
                                            }}>
                                                {monitor.url}
                                            </div> */}
                                        </div>
                                        <div style={{
                                            textAlign: 'right'
                                        }}>
                                            <div style={{
                                                fontSize: '1.1rem',
                                                fontWeight: 'bold',
                                                color: statusInfo.color
                                            }}>
                                                {statusInfo.text}
                                            </div>
                                            <div style={{
                                                fontSize: '1.2rem',
                                                fontWeight: 'bold',
                                                color: uptime >= 99 ? color.success : uptime >= 95 ? color.warning : color.danger
                                            }}>
                                                {uptime.toFixed(2)}% uptime
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recent logs */}
                                    {monitor.logs && monitor.logs.length > 0 && (
                                        <div>
                                            <h5 style={{ margin: '0 0 0.5rem 0' }}>Recent Activity:</h5>
                                            <div style={{
                                                fontSize: '0.8rem',
                                                color: isDark ? '#ccc' : '#666'
                                            }}>
                                                {monitor.logs.slice(0, 3).map((log, index) => (
                                                    <div key={index} style={{ marginBottom: '0.2rem' }}>
                                                        {log.type === 1 ? '🔴' : '🟢'} {formatTimestamp(log.datetime)} 
                                                        {log.duration ? ` (${formatDuration(log.duration)})` : ''}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {isLoading && monitors.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    color: isDark ? '#ccc' : '#666'
                }}>
                    🔄 Loading monitoring data...
                </div>
            )}
        </div>
    );
}
